'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml'

const LogisticFit = dynamic(() => import('../_components/widgets/MLWidgets').then(m => m.LogisticFit), { ssr: false })
const ExampleLearner = dynamic(() => import('../_components/widgets/ExampleLearner'), { ssr: false })

export default function LogisticRegression() {
  return (
    <Lesson>
      <Step>
        <H>Yes-or-no questions</H>
        <P>
          Many problems aren’t “how much?” but “which one?”. Spam or not. Pass or fail. Tumour or not. This is{' '}
          <strong>classification</strong>, and a straight line alone is the wrong tool: a line outputs any number, but we want a
          probability between 0 and 1.
        </P>
        <P>
          The fix: compute the line as before, then squash its output with the <strong>sigmoid</strong> function,{' '}
          <M>{'\\sigma'}</M>:
        </P>
        <MB>{'\\violet{p} = \\sigma(\\green{w}\\blue{x} + \\green{b}) \\qquad \\sigma(z) = \\frac{1}{1 + e^{-z}}'}</MB>
        <Exercise id="sigmoid-plot" title="The S-curve" {...EX.sigmoidPlot} />
        <P>Huge negative inputs give almost 0, huge positive ones almost 1, and 0 gives exactly 0.5. Perfect for a probability.</P>
      </Step>

      <Step gate="q-boundary">
        <H>Fit an S-curve</H>
        <P>
          Below: students, the hours they studied, and whether they <Hl c="amber">passed</Hl> or <Hl c="blue">didn’t</Hl>. The{' '}
          <Hl c="violet">curve</Hl> is the model’s probability of passing. Train it — or set w and b yourself.
        </P>
        <Figure n="6.3" caption="Logistic regression on one feature. The dashed line is the decision boundary — where the model says 50/50.">
          <LogisticFit />
        </Figure>
        <Quiz
          id="q-boundary"
          question={<>Where is the decision boundary — the point where the model says exactly 50%?</>}
          options={[
            { label: <>Where <M>{'wx + b = 0'}</M>, i.e. <M>{'x = -b/w'}</M></>, correct: true },
            { label: <>Where <M>{'x = 0'}</M></>, why: 'Only if b happens to be 0.' },
            { label: <>Where <M>{'w = b'}</M></>, why: 'σ(z) = 0.5 exactly when z = 0, so solve wx + b = 0 for x.' },
          ]}
        />
      </Step>

      <Step>
        <H>The loss and its lovely gradient</H>
        <P>
          The loss is cross-entropy from Module 5, for two classes (<strong>binary cross-entropy</strong>): −ln of the
          probability the model gave to what actually happened.
        </P>
        <MB>{'\\pink{L} = -\\big[\\,y \\ln \\violet{p} + (1-y) \\ln(1-\\violet{p})\\,\\big]'}</MB>
        <P>
          (When <M>{'y = 1'}</M> the second term vanishes, leaving <M>{'-\\ln p'}</M>; when <M>{'y = 0'}</M> only{' '}
          <M>{'-\\ln(1-p)'}</M> remains.) Push this through the chain rule and the sigmoid’s slope <M>{'\\sigma(1-\\sigma)'}</M>{' '}
          cancels out beautifully:
        </P>
        <MB>{'\\pink{\\frac{\\partial L}{\\partial z}} = \\violet{p} - y'}</MB>
        <Callout kind="plain">
          The gradient is just “prediction minus truth”. Predicted 0.9, truth 1 → push up a little (−0.1). Predicted 0.9, truth 0
          → push down hard (0.9). This same tidy result holds for softmax + cross-entropy, which is why that pairing is used
          everywhere.
        </Callout>
      </Step>

      <Step gate="train-logistic">
        <H>Two features: a boundary line</H>
        <P>
          With two inputs, <M>{'w_1 x_1 + w_2 x_2 + b = 0'}</M> is a line across the plane: one side is class 1, the other class
          0. That’s exactly the spam filter from your very first lesson — now you know everything inside it.
        </P>
        <Figure n="6.4" caption="The spam filter from Lesson 0.1, revisited. It’s logistic regression with two features, trained by gradient descent.">
          <ExampleLearner />
        </Figure>
        <Exercise
          id="train-logistic"
          title="Train a 2-D classifier"
          {...EX.trainLogistic}
          hints={[
            <>
              For each example: <C>p = sigmoid(vec.dot(w, X[i]) + b)</C> and <C>e = p - y[i]</C>.
            </>,
            <>
              Accumulate <C>e * X[i][j] / n</C> into each weight’s gradient and <C>e / n</C> into the bias gradient, then step
              against them.
            </>,
          ]}
        />
        <Callout kind="warn">
          Logistic regression can only draw a <em>straight</em> boundary. If the classes are arranged in circles or spirals, no
          straight line can separate them. Solving that is what neural networks are for (Module 7).
        </Callout>
      </Step>
    </Lesson>
  )
}
